/**
 * GradeResultDisplay — Affichage du résultat de correction de copie
 * Suit les patterns Carbon Design System du projet
 */

import type { ComponentType } from 'react'
import { Tag } from '@carbon/react'
import {
  CheckmarkFilled,
  WarningAltFilled,
  ErrorFilled,
  SubtractFilled,
  ThumbsUp,
  IbmWatsonxCodeAssistantForZ,
} from '@carbon/icons-react'
import type { GradeResult, QuestionGradeStatus } from '@sphere/ai/domain'
import styles from './grade-result-display.module.scss'

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────

const STATUS_CONFIG: Record<
  QuestionGradeStatus,
  { icon: ComponentType<{ size?: number; className?: string }>; label: string; type: 'green' | 'warm-gray' | 'red' | 'gray' }
> = {
  correct:   { icon: CheckmarkFilled,  label: 'Correct',    type: 'green'     },
  partial:   { icon: WarningAltFilled, label: 'Partiel',    type: 'warm-gray' },
  incorrect: { icon: ErrorFilled,      label: 'Incorrect',  type: 'red'       },
  missing:   { icon: SubtractFilled,   label: 'Non traité', type: 'gray'      },
}

function getScoreColor(pct: number): string {
  if (pct >= 75) return styles.scoreGreen
  if (pct >= 50) return styles.scoreOrange
  return styles.scoreRed
}

// ─────────────────────────────────────────────
// Props
// ─────────────────────────────────────────────

interface GradeResultDisplayProps {
  result: GradeResult
}

// ─────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────

export function GradeResultDisplay({ result }: GradeResultDisplayProps) {
  const pct = result.percentage

  return (
    <div className={styles.root}>
      {/* ── Note globale ── */}
      <div className={styles.scoreCard}>
        <div className={styles.scoreHeader}>
          <span className={styles.scoreLabel}>Note globale</span>
          <span className={`${styles.scoreValue} ${getScoreColor(pct)}`}>
            {result.total_score.toFixed(1)} / {result.total_points}
          </span>
        </div>

        {/* Barre de progression */}
        <div className={styles.progressBar}>
          <div
            className={`${styles.progressFill} ${getScoreColor(pct)}`}
            style={{ width: `${Math.min(pct, 100)}%` }}
            role="progressbar"
            aria-valuenow={pct}
            aria-valuemin={0}
            aria-valuemax={100}
          />
        </div>
        <span className={styles.scorePercent}>{pct.toFixed(1)} %</span>

        {/* Commentaire global */}
        {result.overall_feedback && (
          <p className={styles.overallFeedback}>{result.overall_feedback}</p>
        )}
      </div>

      {/* ── Forces & Axes d'amélioration ── */}
      {(result.strengths.length > 0 || result.improvements.length > 0) && (
        <div className={styles.insightsGrid}>
          {result.strengths.length > 0 && (
            <div className={styles.insightCard}>
              <div className={styles.insightHeader}>
                <ThumbsUp size={16} className={styles.iconGreen} />
                <span>Points forts</span>
              </div>
              <ul className={styles.insightList}>
                {result.strengths.map((s, i) => (
                  <li key={i}>{s}</li>
                ))}
              </ul>
            </div>
          )}
          {result.improvements.length > 0 && (
            <div className={styles.insightCard}>
              <div className={styles.insightHeader}>
                <IbmWatsonxCodeAssistantForZ size={16} className={styles.iconBlue} />
                <span>Axes d'amélioration</span>
              </div>
              <ul className={styles.insightList}>
                {result.improvements.map((imp, i) => (
                  <li key={i}>{imp}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* ── Détail par question ── */}
      {result.questions.length > 0 && (
        <div className={styles.questionsSection}>
          <h3 className={styles.questionsTitle}>Détail par question</h3>
          <div className={styles.questionsList}>
            {result.questions.map((q) => {
              const cfg = STATUS_CONFIG[q.status as QuestionGradeStatus]
              const Icon = cfg.icon
              return (
                <div key={q.question_number} className={styles.questionCard}>
                  {/* En-tête question */}
                  <div className={styles.questionHeader}>
                    <div className={styles.questionMeta}>
                      <span className={styles.questionNum}>
                        Q{q.question_number}
                      </span>
                      <span className={styles.questionTitle}>
                        {q.question_title}
                      </span>
                    </div>
                    <div className={styles.questionScore}>
                      <Tag type={cfg.type} size="sm">
                        <Icon size={12} />
                        &nbsp;{cfg.label}
                      </Tag>
                      <span className={styles.questionPoints}>
                        {q.points_earned} / {q.points_possible} pt
                        {q.points_possible > 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>

                  {/* Corps question */}
                  <div className={styles.questionBody}>
                    {q.expected_answer && (
                      <div className={styles.answerBlock}>
                        <span className={styles.answerLabel}>Attendu</span>
                        <p className={styles.answerText}>{q.expected_answer}</p>
                      </div>
                    )}
                    {q.student_answer && (
                      <div className={styles.answerBlock}>
                        <span className={styles.answerLabel}>Élève</span>
                        <p className={styles.answerText}>{q.student_answer}</p>
                      </div>
                    )}
                    {q.feedback && (
                      <div className={styles.feedbackBlock}>
                        <p className={styles.feedbackText}>💬 {q.feedback}</p>
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
